import React, { useState, useEffect, useRef, useCallback } from 'react'
import '../../styles/content/ContentList.css'
import '../../styles/CreateOwnContent.css'
import { deleteTemplate, listTemplates, setTemplateVisibility } from '../../server/server'
import { useUser } from '../../auth'
import ConfirmationModal from '../modals/ConfirmationModal'
import { format_day, format_date_long } from '../../utils/format_day'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { HiOutlineSearch, HiOutlineUserGroup, HiX, HiOutlineCollection } from 'react-icons/hi'
import { notify, prefersReducedMotion, translateTargetText } from '../../utils/util'
import InfiniteScroll from 'react-infinite-scroll-component'
import Loading from '../Loading'
import Avatar from '../common/Avatar'
import TemplateActionsMenu from './templates/TemplateActionsMenu'
import { TEMPLATE_SCOPES, VISIBILITY_PRIVATE, VISIBILITY_SCHOOL, authorLabel, isShared } from './templates/templateUtils'

// Cards past this index share the same entrance delay (same cap as the classroom).
const STAGGER_CAP = 12
const SKELETON_CARDS = 4
const HIGHLIGHT_MS = 1400
const SEARCH_DEBOUNCE_MS = 300
const PREVIEW_CHARS = 160

const SCOPE_LABEL_KEYS = { mine: 'myTemplates', school: 'schoolTemplates' }

/**
 * Stamp each item with its position inside the page it arrived in. The stagger
 * reads this instead of the list index so appended pages start from zero and
 * cards already on screen never change their animation delay.
 */
const withEnterIndex = (list = []) => list.map((item, i) => ({ ...item, _enter: Math.min(i, STAGGER_CAP) }))

/**
 * "Use template" library: the user's own templates and the ones colleagues
 * shared with the school. `scope` is owned by the parent so it survives the
 * library <-> form swap (e.g. returning to the right tab after an edit).
 */
function ContentTemplate({ setSelTemplate, type, selectTemplateToEdit = () => {}, focusTemplateId = null, scope = 'mine', onScopeChange = () => {} }) {
    const { user } = useUser()
    const { t } = useTranslation()

    const [contents, setContent] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [highlightId, setHighlightId] = useState(null)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    // { kind: 'delete' | 'unshare', template } while a confirmation is open.
    const [pendingAction, setPendingAction] = useState(null)
    const listRef = useRef(null)
    const tabRefs = useRef({})
    const mountedRef = useRef(true)
    const focusHandledRef = useRef(false)
    // Guards against a slow response for an old tab/search landing after a newer one.
    const requestRef = useRef(0)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchInput])

    const loadPage = useCallback(
        async (loaded) => {
            const requestId = ++requestRef.current
            const firstPage = loaded === 0
            if (firstPage) setLoading(true)
            else setLoadingMore(true)
            try {
                const resp = await listTemplates({ type, scope, loaded, text: search })
                if (!mountedRef.current || requestId !== requestRef.current) return
                if (resp?.status === 200) {
                    const total = resp?.data?.size ?? 0
                    const page = withEnterIndex(resp?.data?.list ?? [])
                    setContent((prev) => {
                        const next = firstPage ? page : [...prev, ...page]
                        setHasMore(total > next.length)
                        return next
                    })
                } else if (firstPage) {
                    setContent([])
                    setHasMore(false)
                }
            } finally {
                if (mountedRef.current && requestId === requestRef.current) {
                    setLoading(false)
                    setLoadingMore(false)
                }
            }
        },
        [type, scope, search]
    )

    // New tab or search: start over from the first page.
    useEffect(() => {
        setContent([])
        setHasMore(false)
        loadPage(0)
    }, [loadPage])

    // Scroll the just-updated template into view once the first page is in.
    useEffect(() => {
        if (!focusTemplateId || loading || focusHandledRef.current) return
        const card = listRef.current?.querySelector(`[data-template-id="${focusTemplateId}"]`)
        if (!card) return
        focusHandledRef.current = true
        card.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' })
        setHighlightId(focusTemplateId)
    }, [focusTemplateId, loading, contents])

    // Separate from the scroll effect so a page append cannot cancel the timer.
    useEffect(() => {
        if (!highlightId) return
        const timer = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS)
        return () => clearTimeout(timer)
    }, [highlightId])

    const loadMore = () => {
        if (!loading && !loadingMore) loadPage(contents.length)
    }

    const changeScope = (next) => {
        if (next === scope) return
        onScopeChange(next)
    }

    // Roving focus between the two tabs, like a native tablist.
    const onTabKeyDown = (event) => {
        const isRTL = (i18n.language || '').startsWith('ar')
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
        let next = null
        if (step) {
            const index = TEMPLATE_SCOPES.indexOf(scope)
            next = TEMPLATE_SCOPES[(index + (isRTL ? -step : step) + TEMPLATE_SCOPES.length) % TEMPLATE_SCOPES.length]
        }
        if (event.key === 'Home') next = TEMPLATE_SCOPES[0]
        if (event.key === 'End') next = TEMPLATE_SCOPES[TEMPLATE_SCOPES.length - 1]
        if (!next) return
        event.preventDefault()
        changeScope(next)
        tabRefs.current[next]?.focus()
    }

    const replaceTemplate = (id, patch) => setContent((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)))
    const dropTemplate = (id) => setContent((prev) => prev.filter((c) => c.id !== id))

    const applyVisibility = async (template, visibility) => {
        const resp = await setTemplateVisibility({ type, id: template.id, visibility })
        if (!mountedRef.current) return false
        if (resp?.status === 200) {
            notify(t(visibility === VISIBILITY_SCHOOL ? 'templateShared' : 'templateUnshared'))
            // An unshared template no longer belongs in the school list.
            if (scope === 'school' && visibility !== VISIBILITY_SCHOOL) dropTemplate(template.id)
            else replaceTemplate(template.id, { visibility })
            return true
        }
        notify(t('templateIsNotUpdated'), 'error')
        return false
    }

    // Sharing is low-risk and immediate; stopping sharing hides it from colleagues, so confirm.
    const toggleShare = useCallback(
        (template) => {
            if (isShared(template)) setPendingAction({ kind: 'unshare', template })
            else applyVisibility(template, VISIBILITY_SCHOOL)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [type, scope]
    )
    const askDelete = useCallback((template) => setPendingAction({ kind: 'delete', template }), [])

    const closeConfirmation = () => setPendingAction(null)

    const confirmPendingAction = async () => {
        const action = pendingAction
        if (!action) return
        if (action.kind === 'unshare') {
            await applyVisibility(action.template, VISIBILITY_PRIVATE)
        } else {
            const resp = await deleteTemplate({ type, id: action.template.id })
            if (!mountedRef.current) return
            if (resp?.status === 200) {
                notify(t('templateDeleted'))
                dropTemplate(action.template.id)
            } else {
                notify(t('templateIsNotDeleted'), 'error')
            }
        }
        if (mountedRef.current) setPendingAction(null)
    }

    const confirmationCopy = (() => {
        if (!pendingAction) return { message: '', subMessage: '' }
        if (pendingAction.kind === 'unshare') return { message: t('stopSharingTemplateQuestion'), subMessage: t('stopSharingTemplateBody') }
        if (isShared(pendingAction.template)) return { message: t('doYouWantToDeleteThis'), subMessage: t('deleteSharedTemplateBody') }
        return { message: t('doYouWantToDeleteThis'), subMessage: t('deleteTemplateBody') }
    })()

    const showSkeleton = loading && contents.length === 0
    const isEmpty = !loading && contents.length === 0

    return (
        <div className="w-full mt-5" ref={listRef}>
            <div className="coc-library-bar flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
                <div className="coc-scope-tabs flex" role="tablist" aria-label={t('useTemplate')} onKeyDown={onTabKeyDown}>
                    {TEMPLATE_SCOPES.map((value) => {
                        const selected = value === scope
                        return (
                            <button
                                key={value}
                                ref={(node) => (tabRefs.current[value] = node)}
                                type="button"
                                role="tab"
                                id={`template-scope-${value}`}
                                aria-selected={selected}
                                aria-controls="template-library-panel"
                                tabIndex={selected ? 0 : -1}
                                className="coc-scope-tab"
                                onClick={() => changeScope(value)}
                            >
                                {t(SCOPE_LABEL_KEYS[value])}
                            </button>
                        )
                    })}
                </div>
                <label className="coc-search coc-input flex items-center gap-2 rounded-full border border-solid border-deselectPurple bg-white px-4 h-[40px] mb-2">
                    <HiOutlineSearch size={18} className="text-mudPurple flex-shrink-0" aria-hidden="true" />
                    <input
                        type="search"
                        id="template-search-input"
                        className="flex-1 min-w-0 bg-transparent outline-none text-deepPurple text-base placeholder-mudPurple"
                        placeholder={t('searchTemplates')}
                        aria-label={t('searchTemplates')}
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                    />
                    {!!searchInput && (
                        <button type="button" className="coc-icon-button text-mudPurple" aria-label={t('clearSearch')} onClick={() => setSearchInput('')}>
                            <HiX size={16} aria-hidden="true" />
                        </button>
                    )}
                </label>
            </div>

            <div id="template-library-panel" role="tabpanel" aria-labelledby={`template-scope-${scope}`} aria-busy={loading} className="mt-5">
                {showSkeleton ? (
                    <TemplateSkeleton />
                ) : isEmpty ? (
                    <EmptyLibrary scope={scope} search={search} />
                ) : (
                    <InfiniteScroll
                        dataLength={contents.length}
                        next={loadMore}
                        hasMore={hasMore}
                        scrollableTarget="scroll-inf-target"
                        className="coc-template-grid"
                        style={{ overflow: 'visible' }}
                        scrollThreshold={0.8}
                        loader={
                            <div className="coc-grid-full flex justify-center py-4" aria-hidden="true">
                                <Loading />
                            </div>
                        }
                    >
                        {contents.map((content) => (
                            <Template
                                key={content.id}
                                content={content}
                                scope={scope}
                                user={user}
                                onUse={setSelTemplate}
                                onEdit={selectTemplateToEdit}
                                onToggleShare={toggleShare}
                                onDelete={askDelete}
                                highlighted={highlightId === content.id}
                            />
                        ))}
                    </InfiniteScroll>
                )}
            </div>

            <ConfirmationModal
                modalIsOpen={!!pendingAction}
                closeModal={closeConfirmation}
                confirmation={confirmPendingAction}
                message={confirmationCopy.message}
                subMessage={confirmationCopy.subMessage}
                destructive={pendingAction?.kind !== 'unshare'}
                confirmButtonText={pendingAction?.kind === 'unshare' ? 'stopSharingTemplate' : 'OK'}
            />
        </div>
    )
}

const EmptyLibrary = ({ scope, search }) => {
    const { t } = useTranslation()
    const Icon = scope === 'school' ? HiOutlineUserGroup : HiOutlineCollection
    let title = t(scope === 'school' ? 'schoolTemplatesEmptyTitle' : 'myTemplatesEmptyTitle')
    let body = t(scope === 'school' ? 'schoolTemplatesEmptyBody' : 'myTemplatesEmptyBody')
    if (search) {
        title = t('templatesNoMatchTitle', { search })
        body = t('templatesNoMatchBody')
    }
    return (
        <div className="coc-empty coc-fade-in flex flex-col items-center text-center rounded-xl px-6 py-14">
            <span className="coc-empty-icon flex items-center justify-center rounded-full" aria-hidden="true">
                <Icon size={26} />
            </span>
            <p className="text-deepPurple font-bold text-lg mt-4">{title}</p>
            <p className="text-mudPurple text-base mt-1 max-w-[420px]">{body}</p>
        </div>
    )
}

const Template = React.memo(function Template({ content, scope, user, onUse, onEdit, onToggleShare, onDelete, highlighted = false }) {
    const { t } = useTranslation()
    const [showMore, setShowMore] = useState(false)
    // Survey templates keep their intro in `description`.
    const message = content?.message ?? content?.description ?? ''
    const shared = isShared(content)
    const isSurvey = !content?.type || content?.type === 'survey'

    return (
        <div
            className={`coc-card p-4 bg-[#F8F8FB] rounded-xl flex flex-col ${highlighted ? 'coc-card-highlight' : ''}`}
            id={content.id}
            data-template-id={content.id}
            style={{ '--i': content._enter ?? 0 }}
        >
            <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-deepPurple font-bold text-xl text-left flex-1 min-w-0 break-words">{content?.title}</h3>
                    {content?.can_manage && (
                        <TemplateActionsMenu
                            templateId={content.id}
                            shared={shared}
                            onEdit={() => onEdit(content)}
                            onToggleShare={() => onToggleShare(content)}
                            onDelete={() => onDelete(content)}
                        />
                    )}
                </div>

                {scope === 'school' ? (
                    <div className="coc-author flex items-center gap-2 mt-2 min-w-0">
                        <Avatar firstName={content?.author?.first_name} lastName={content?.author?.last_name} size={28} />
                        <p className="text-sm text-mudPurple min-w-0 truncate">
                            {t('templateCreatedBy')} <span className="font-bold text-deepPurple">{authorLabel(content?.author, user, t) || t('templateAuthorUnknown')}</span>
                            {!!content?.created_date && <span> · {format_date_long(content.created_date, i18n.language)}</span>}
                        </p>
                    </div>
                ) : (
                    shared && (
                        <div className="mt-2">
                            <span className="coc-shared-badge inline-flex items-center gap-1.5 rounded-full text-[13px] font-semibold px-2.5 py-1">
                                <HiOutlineUserGroup size={14} aria-hidden="true" />
                                {t('sharedWithSchool')}
                            </span>
                        </div>
                    )
                )}

                {!!message && (
                    <p className="text-[#413F5E] leading-[22px] text-left mt-3 text-[16px] break-words">
                        {showMore ? message : message.substring(0, PREVIEW_CHARS)}
                        {!showMore && message.length > PREVIEW_CHARS && '...'}
                        {message.length > PREVIEW_CHARS && (
                            <button
                                type="button"
                                className="coc-link font-bold text-[#5E639B] pointer text-[14px] block"
                                onClick={() => setShowMore((prev) => !prev)}
                                aria-expanded={showMore}
                            >
                                {!showMore ? t('showMore') : t('showLess')}
                            </button>
                        )}
                    </p>
                )}

                {!isSurvey && (
                    <div className="flex flex-col flex-1 justify-end mt-2">
                        <div className="flex justify-between items-center gap-3 my-2">
                            <span className="text-[#5E639B] font-semibold text-base">{t('targetGroup')} </span>
                            <div className="text-[#5E639B] font-semibold text-base flex items-center justify-center bg-white rounded-full py-2 px-3 min-w-[130px] text-center">
                                {translateTargetText(content?.target_text) ?? t('Select target')}
                            </div>
                        </div>
                        <div className="flex justify-between items-center gap-3 my-2">
                            <span className="text-[#5E639B] font-semibold text-base">{t('schedule')} </span>
                            <div className="text-[#5E639B] font-semibold flex items-center justify-center text-base bg-white rounded-full py-2 px-3 min-w-[130px]">
                                {content?.schedule ? format_day(content?.publicationDate, true, i18n.language) : t('off')}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex justify-center items-center w-full mt-3">
                <button
                    type="button"
                    className="coc-button coc-button-primary rounded-full py-3 px-6 min-w-[140px] flex items-center justify-center font-semibold pointer"
                    onClick={() => onUse(content)}
                    id={`select-template-button-${content.id}`}
                >
                    {t('useThisTemplate')}
                </button>
            </div>
        </div>
    )
})

/** First-load placeholder shaped like the real cards, so the grid doesn't jump. */
const TemplateSkeleton = () => (
    <div className="coc-template-grid" aria-hidden="true">
        {Array.from({ length: SKELETON_CARDS }, (_, i) => (
            <div key={i} className="coc-card coc-skeleton-card p-4 rounded-xl flex flex-col" style={{ '--i': i }}>
                <div className="coc-skeleton-block coc-skeleton-title" />
                <div className="coc-skeleton-block coc-skeleton-meta" />
                <div className="coc-skeleton-block coc-skeleton-line" />
                <div className="coc-skeleton-block coc-skeleton-line" style={{ width: '70%' }} />
                <div className="flex justify-between items-center my-2 mt-6">
                    <div className="coc-skeleton-block coc-skeleton-label" />
                    <div className="coc-skeleton-block coc-skeleton-pill" />
                </div>
                <div className="flex justify-between items-center my-2">
                    <div className="coc-skeleton-block coc-skeleton-label" />
                    <div className="coc-skeleton-block coc-skeleton-pill" />
                </div>
                <div className="flex justify-center mt-3">
                    <div className="coc-skeleton-block coc-skeleton-button" />
                </div>
            </div>
        ))}
    </div>
)

export default ContentTemplate
